import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useInboxStore } from '../../store/inboxStore';
import conversationService from '../../services/whatsapp/conversationService';
import messageService from '../../services/whatsapp/messageService';
import cannedReplyService from '../../services/whatsapp/cannedReplyService';
import settingsService from '../../services/whatsapp/settingsService';
import contactService from '../../services/whatsapp/contactService';
import { apiClient } from '../../services/whatsapp/apiClient';
import {
  Agent,
  CannedReply,
  Contact,
  Conversation,
  ConversationNote,
  ConversationStatus,
  Message,
  Template,
  WhatsAppSettings,
  DEFAULT_WHATSAPP_SETTINGS,
  canSendFreeform
} from '../../types/models/whatsapp';
import ConversationList from './inbox/ConversationList';
import ChatCanvas from './inbox/ChatCanvas';
import Composer from './inbox/Composer';
import ContextPanel from './inbox/ContextPanel';
import TemplatePicker from './inbox/TemplatePicker';
import CannedReplyManager from './inbox/CannedReplyManager';
import ChatOptionsMenu from './inbox/ChatOptionsMenu';
import SettingsPanel from './inbox/SettingsPanel';
import NewChatDialog from './inbox/NewChatDialog';

/**
 * Team Inbox container.
 *
 * Owns the Firestore subscriptions and passes plain data down — the presentation
 * components stay unaware of the data layer so they can be tested in isolation.
 */
const WhatsAppInbox: React.FC = () => {
  const { adminProfile } = useAuth();
  const {
    selectedConversationId,
    filters,
    drafts,
    contextPanelOpen,
    selectConversation,
    setFilters,
    setDraft,
    clearDraft,
    toggleContextPanel
  } = useInboxStore();

  // Deep link from the sent-message log: /whatsapp?conversation=<id> opens
  // that thread directly. Consumed once, then stripped from the URL so a
  // later manual selection is not overridden on re-render.
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const requested = searchParams.get('conversation');
    if (!requested) return;

    selectConversation(requested);
    searchParams.delete('conversation');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, selectConversation]);

  const [rawConversations, setRawConversations] = useState<Conversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  /** Pages older than the live subscription window, oldest-first. */
  const [history, setHistory] = useState<Message[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [notes, setNotes] = useState<ConversationNote[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [cannedReplies, setCannedReplies] = useState<CannedReply[]>([]);
  const [activeContact, setActiveContact] = useState<Contact | null>(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [cannedManagerOpen, setCannedManagerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [settings, setSettings] = useState<WhatsAppSettings>(
    DEFAULT_WHATSAPP_SETTINGS
  );
  const [error, setError] = useState<string | null>(null);

  const agentId = adminProfile?.uid || null;

  /**
   * Only `status` and `assigneeId` shape the Firestore query; `search`, `tags`,
   * and `windowState` are applied client-side. Depending on the whole `filters`
   * object would tear down and recreate the listener on every keystroke, which
   * both wastes reads and trips a WatchChangeAggregator assertion in the SDK
   * (firebase-js-sdk#9267) when listeners churn faster than the watch stream
   * settles.
   */
  const queryStatus = filters.status;
  const queryAssignee = filters.assigneeId;

  useEffect(() => {
    setConversationsLoading(true);
    const unsubscribe = conversationService.subscribeToConversations(
      { status: queryStatus, assigneeId: queryAssignee },
      agentId,
      (result) => {
        setRawConversations(result);
        setConversationsLoading(false);
      },
      (err) => {
        setError(err.message);
        setConversationsLoading(false);
      }
    );
    return unsubscribe;
  }, [queryStatus, queryAssignee, agentId]);

  // Client-side refinement runs on the already-subscribed data, so typing in
  // the search box never touches the listener.
  const conversations = useMemo(
    () => conversationService.applyClientFilters(rawConversations, filters),
    [rawConversations, filters]
  );

  // Active conversation + its thread and notes.
  useEffect(() => {
    if (!selectedConversationId) {
      setActiveConversation(null);
      setMessages([]);
      setNotes([]);
      return;
    }

    setMessagesLoading(true);
    // History belongs to the previous thread — drop it before the new one loads.
    setHistory([]);
    setHasMoreHistory(true);

    const unsubConversation = conversationService.subscribeToConversation(
      selectedConversationId,
      setActiveConversation
    );

    const unsubMessages = messageService.subscribeToMessages(
      selectedConversationId,
      (result) => {
        setMessages(result);
        setMessagesLoading(false);
      },
      (err) => {
        setError(err.message);
        setMessagesLoading(false);
      }
    );

    const unsubNotes = conversationService.subscribeToNotes(
      selectedConversationId,
      setNotes
    );

    // Clearing unread is best-effort — a failure here must not block the thread.
    conversationService.markRead(selectedConversationId).catch(() => {});

    return () => {
      unsubConversation();
      unsubMessages();
      unsubNotes();
    };
  }, [selectedConversationId]);

  // Presence heartbeat every 30s when inbox is open
  useEffect(() => {
    const sendHeartbeat = () => {
      apiClient.post('/presence').catch(() => {});
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(interval);
  }, []);

  // Agents come from the existing admins collection — no separate agent store.
  useEffect(() => {
    if (!db) return;

    let cancelled = false;
    const loadAgents = async () => {
      try {
        const snapshot = await getDocs(
          query(collection(db!, 'admins'), where('active', '==', true))
        );
        if (cancelled) return;
        setAgents(
          snapshot.docs.map((d) => ({
            id: d.data().uid || d.id,
            name: d.data().displayName || d.data().email || 'Agent',
            email: d.data().email || ''
          }))
        );
      } catch (err) {
        console.error('❌ Failed to load agents:', err);
      }
    };

    loadAgents();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live contact record for the open thread — block and opt-out state must
  // reflect a webhook-driven change (an inbound STOP) without a refresh.
  useEffect(() => {
    const contactId = activeConversation?.contactId;
    if (!contactId) {
      setActiveContact(null);
      return;
    }
    return conversationService.subscribeToContact(contactId, setActiveContact);
  }, [activeConversation?.contactId]);

  // Canned replies: team-wide plus this agent's personal ones.
  useEffect(() => {
    const unsubscribe = cannedReplyService.subscribe(
      agentId,
      setCannedReplies,
      (err) => console.error('❌ Failed to load canned replies:', err)
    );
    return unsubscribe;
  }, [agentId]);

  // Account-wide toggles, live so one admin's change reaches every open inbox.
  useEffect(() => settingsService.subscribe(setSettings), []);

  // Contact directory, for the new-chat picker.
  useEffect(
    () =>
      contactService.subscribe(setContacts, (err) =>
        console.error('❌ Failed to load contacts:', err)
      ),
    []
  );

  const windowOpen = activeConversation
    ? canSendFreeform(activeConversation)
    : false;

  // Mirrors the server's canMessageContact guard so the composer explains the
  // block up front rather than failing at send time.
  const sendBlockedReason = activeContact?.blocked
    ? 'This contact is blocked. Unblock them from chat options to send messages.'
    : activeContact?.optIn.status === 'opted_out'
      ? 'This contact has opted out of WhatsApp messages.'
      : null;

  /**
   * Older pages, merged ahead of the live window.
   *
   * Deduplicated by id: the archive query is bounded by the oldest live message
   * rather than an exclusive cursor, so a page can overlap the subscription.
   */
  const thread = useMemo(() => {
    if (history.length === 0) return messages;

    const liveIds = new Set(messages.map((m) => m.id));
    const older = history.filter((m) => !liveIds.has(m.id));
    return [...older, ...messages];
  }, [history, messages]);

  const handleLoadHistory = useCallback(async () => {
    if (!selectedConversationId || historyLoading || !hasMoreHistory) return;

    // Page backwards from the oldest message currently rendered.
    const oldest = history[0] ?? messages[0];
    if (!oldest) return;

    setHistoryLoading(true);
    try {
      const older = await messageService.loadHistory(
        selectedConversationId,
        oldest.createdAt
      );

      if (older.length === 0) {
        setHasMoreHistory(false);
      } else {
        setHistory((current) => [...older, ...current]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load earlier messages');
      // Stop retrying on every scroll event after a failure.
      setHasMoreHistory(false);
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedConversationId, historyLoading, hasMoreHistory, history, messages]);

  const handleSendText = useCallback(
    async (text: string) => {
      if (!selectedConversationId) return;
      await messageService.send({
        conversationId: selectedConversationId,
        type: 'text',
        text
      });
      clearDraft(selectedConversationId);
    },
    [selectedConversationId, clearDraft]
  );

  const handleSendTemplate = useCallback(
    async (
      template: Template,
      variables: Record<string, string>,
      header?: { type: 'image' | 'video' | 'document'; link: string }
    ) => {
      if (!selectedConversationId) return;
      await messageService.send({
        conversationId: selectedConversationId,
        type: 'template',
        template: {
          name: template.name,
          language: template.language,
          variables,
          // Only set for media headers; a template without one must not carry
          // a header component or Meta rejects the send.
          ...(header ? { header } : {})
        }
      });
    },
    [selectedConversationId]
  );

  /**
   * Two-step upload: the server issues a presigned R2 PUT, the browser uploads
   * directly, then we send the message referencing the stored key. Keeps large
   * files off the API server entirely.
   */
  const handleAttach = useCallback(
    async (file: File) => {
      if (!selectedConversationId) return;

      const { uploadUrl, r2Key } = await apiClient.post<{
        uploadUrl: string;
        r2Key: string;
      }>('/media/upload-url', {
        filename: file.name,
        mimeType: file.type,
        size: file.size
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      await messageService.send({
        conversationId: selectedConversationId,
        type: file.type.startsWith('image/') ? 'image' : 'document',
        media: {
          r2Key,
          mimeType: file.type,
          filename: file.name,
          size: file.size
        }
      });
    },
    [selectedConversationId]
  );

  const handleAssign = useCallback(
    async (newAgentId: string | null) => {
      if (!selectedConversationId) return;
      await conversationService.assignConversation(selectedConversationId, newAgentId);
    },
    [selectedConversationId]
  );

  const handleStatusChange = useCallback(
    async (status: ConversationStatus) => {
      if (!selectedConversationId) return;
      await conversationService.setStatus(selectedConversationId, status);
    },
    [selectedConversationId]
  );

  const handleAddNote = useCallback(
    async (body: string) => {
      if (!selectedConversationId) return;
      await conversationService.addNote(selectedConversationId, body);
    },
    [selectedConversationId]
  );

  const handleSetTags = useCallback(
    async (tags: string[]) => {
      if (!selectedConversationId) return;
      await conversationService.setTags(selectedConversationId, tags);
    },
    [selectedConversationId]
  );

  // Autocomplete source — tags already in use across the loaded conversations.
  const suggestedTags = useMemo(() => {
    const seen = new Set<string>();
    rawConversations.forEach((c) => c.tags.forEach((tag) => seen.add(tag)));
    return Array.from(seen).sort();
  }, [rawConversations]);

  const handleSetBotEnabled = useCallback(
    async (enabled: boolean) => {
      if (!selectedConversationId) return;
      await conversationService.setBotEnabled(selectedConversationId, enabled);
    },
    [selectedConversationId]
  );

  const handleClearBotSession = useCallback(async () => {
    if (!selectedConversationId) return;
    await conversationService.clearBotSession(selectedConversationId);
  }, [selectedConversationId]);

  const handleSetBlocked = useCallback(
    async (blocked: boolean) => {
      if (!activeConversation) return;
      await conversationService.setContactBlocked(
        activeConversation.contactId,
        blocked
      );
    },
    [activeConversation]
  );

  const handleSetOptedOut = useCallback(
    async (optedOut: boolean) => {
      if (!activeConversation) return;
      await conversationService.setContactOptIn(
        activeConversation.contactId,
        optedOut ? 'opted_out' : 'opted_in'
      );
    },
    [activeConversation]
  );

  const handleRetry = useCallback(
    (messageId: string) => {
      if (!selectedConversationId) return;
      messageService
        .retry(messageId, selectedConversationId)
        .catch((err) => setError(err.message));
    },
    [selectedConversationId]
  );

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      {error && (
        <div className="bg-danger-50 px-4 py-2 text-sm text-danger-600">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex min-h-0 flex-1 relative pt-12">
        {/* Conversation list — hidden on mobile once a thread is open. */}
        <div
          className={`w-full shrink-0 md:w-80 ${
            selectedConversationId ? 'hidden md:block' : 'block'
          }`}
        >
          <ConversationList
            conversations={conversations}
            selectedId={selectedConversationId}
            loading={conversationsLoading}
            filters={filters}
            onSelect={selectConversation}
            onFiltersChange={setFilters}
            onManageCannedReplies={() => setCannedManagerOpen(true)}
            onOpenSettings={() => setSettingsOpen(true)}
            onNewChat={() => setNewChatOpen(true)}
          />
        </div>

        <div
          className={`min-w-0 flex-1 ${
            selectedConversationId ? 'flex' : 'hidden md:flex'
          }`}
        >
          <ChatCanvas
            conversation={activeConversation}
            messages={thread}
            loading={messagesLoading}
            hasMoreHistory={hasMoreHistory}
            loadingHistory={historyLoading}
            onLoadHistory={handleLoadHistory}
            onBack={() => selectConversation(null)}
            onToggleContextPanel={toggleContextPanel}
            onRetry={handleRetry}
            headerActions={
              activeConversation && (
                <ChatOptionsMenu
                  conversation={activeConversation}
                  contactBlocked={activeContact ? activeContact.blocked === true : null}
                  contactOptedOut={activeContact?.optIn.status === 'opted_out'}
                  onStatusChange={handleStatusChange}
                  onSetBotEnabled={handleSetBotEnabled}
                  onClearBotSession={handleClearBotSession}
                  onSetBlocked={handleSetBlocked}
                  onSetOptedOut={handleSetOptedOut}
                />
              )
            }
          >
            {activeConversation && (
              <Composer
                conversation={activeConversation}
                windowOpen={windowOpen}
                draft={drafts[activeConversation.id] || ''}
                cannedReplies={cannedReplies}
                agentName={adminProfile?.displayName || adminProfile?.email || 'Agent'}
                sendBlockedReason={sendBlockedReason}
                onDraftChange={(text) => setDraft(activeConversation.id, text)}
                onSendText={handleSendText}
                onSendTemplate={handleSendTemplate}
                onOpenTemplatePicker={() => setTemplatePickerOpen(true)}
                onAttach={handleAttach}
                onCannedReplyUsed={(id) => cannedReplyService.recordUse(id)}
              />
            )}
          </ChatCanvas>
        </div>

        {/* Context panel — slide-over below lg, fixed column above. */}
        {activeConversation && contextPanelOpen && (
          <div className="fixed inset-y-0 right-0 z-40 w-80 shadow-xl lg:static lg:z-auto lg:shadow-none">
            <ContextPanel
              conversation={activeConversation}
              notes={notes}
              agents={agents}
              onClose={toggleContextPanel}
              onAssign={handleAssign}
              onStatusChange={handleStatusChange}
              onAddNote={handleAddNote}
              onSetTags={handleSetTags}
              suggestedTags={suggestedTags}
            />
          </div>
        )}
      </div>

      <TemplatePicker
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        onSend={handleSendTemplate}
      />

      <CannedReplyManager
        open={cannedManagerOpen}
        replies={cannedReplies}
        agentId={agentId}
        onClose={() => setCannedManagerOpen(false)}
        onCreate={(input) => cannedReplyService.create(input).then(() => {})}
        onUpdate={(id, input) => cannedReplyService.update(id, input)}
        onDelete={(id) => cannedReplyService.remove(id)}
      />

      <NewChatDialog
        open={newChatOpen}
        contacts={contacts}
        onClose={() => setNewChatOpen(false)}
        onStart={async (phone, name) => {
          const result = await contactService.startConversation(phone, name);
          return result.conversationId;
        }}
        onOpenConversation={selectConversation}
      />

      <SettingsPanel
        open={settingsOpen}
        settings={settings}
        onClose={() => setSettingsOpen(false)}
        onUpdate={(updates) => settingsService.update(updates).then(() => {})}
      />
    </div>
  );
};

export default WhatsAppInbox;
